import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { Activity, Wrench, Clock, LogIn, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";

/** Very small inline markdown — bold, italic, links, and line breaks. */
function safeHref(url) {
  // Block javascript:, data:, vbscript: etc. Allow http(s), mailto, tel, and relative.
  const s = String(url || "").trim();
  if (/^\s*(javascript|data|vbscript|file):/i.test(s)) return "#";
  return s;
}

function renderInline(text) {
  if (!text) return null;
  const html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="text-slate-900">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, url) =>
      `<a href="${safeHref(url)}" target="_blank" rel="noopener noreferrer" class="text-[#0D9488] underline underline-offset-2 hover:text-[#0F766E]">${label}</a>`
    )
    .replace(/\n/g, "<br />");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function Maintenance() {
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const brand = settings?.primary_color || "#0D9488";

  return (
    <div className="min-h-screen bg-white text-slate-900 noise-overlay flex items-center justify-center px-6 py-12" data-testid="maintenance-page">
      <div className="max-w-2xl w-full">
        {/* Brand strip */}
        <div className="flex items-center gap-3 mb-10">
          {settings?.logo_url ? (
            <div className="w-10 h-10 border border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50">
              <img src={settings.logo_url} alt="" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-10 h-10 border border-slate-300 flex items-center justify-center" style={{ backgroundColor: `${brand}1a` }}>
              <Activity className="w-4 h-4" style={{ color: brand }} strokeWidth={2.5} />
            </div>
          )}
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-xl tracking-tight uppercase">{settings?.site_name || "The Amino Exchange"}</span>
            {settings?.tagline && (
              <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-[0.25em] mt-1">{settings.tagline}</span>
            )}
          </div>
        </div>

        {/* Status chip */}
        <div className="inline-flex items-center gap-2 border px-3 py-1 mb-6 font-mono-num text-[10px] uppercase tracking-[0.3em]"
             style={{ borderColor: `${brand}66`, color: brand, backgroundColor: `${brand}12` }}>
          <Wrench className="w-3 h-3" />
          <span>// Status · Maintenance</span>
        </div>

        {/* Title */}
        <h1
          className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-slate-900 leading-[1.05]"
          data-testid="maintenance-title"
        >
          {settings?.maintenance_title || "Scheduled maintenance"}
        </h1>

        {/* Body */}
        <div className="mt-5 text-slate-600 text-base leading-relaxed" data-testid="maintenance-body">
          {renderInline(settings?.maintenance_body || "We're rolling out an update. Please check back shortly.")}
        </div>

        {/* ETA */}
        {settings?.maintenance_eta && (
          <div className="mt-6 inline-flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2" data-testid="maintenance-eta">
            <Clock className="w-3.5 h-3.5" style={{ color: brand }} />
            <span className="font-mono-num text-[11px] uppercase tracking-widest text-slate-600">ETA</span>
            <span className="text-sm text-slate-900">{settings.maintenance_eta}</span>
          </div>
        )}

        {/* Divider */}
        <div className="my-10 h-px bg-zinc-800" />

        {/* Auth aware actions */}
        <div className="flex flex-wrap items-center gap-3">
          {user ? (
            <>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
                Signed in as <span className="text-slate-800">{user.email}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800 h-9"
                onClick={logout}
                data-testid="maintenance-signout"
              >
                <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
              </Button>
            </>
          ) : (
            <Button
              asChild
              className="rounded-none text-black uppercase tracking-widest font-semibold h-10 px-5"
              style={{ backgroundColor: brand }}
              data-testid="maintenance-signin"
            >
              <Link to="/login">
                <LogIn className="w-3.5 h-3.5 mr-1.5" /> Admin sign in
              </Link>
            </Button>
          )}
        </div>

        <div className="mt-8 font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-400">
          // The Amino Exchange · Precision Peptide Protocol
        </div>
      </div>
    </div>
  );
}
